import { useState } from 'react';
import type { KanbanCartao, KanbanQuadro, NivelPrioridadeKanban } from '@pieltec/contracts';
import { CheckCircle2, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { destinosMoverPara, type DestinoMover } from '../../../lib/kanban/movimento';
import { useKanbanAcoes } from './kanban-acoes-context';
import { KanbanBloqueioDialog } from './kanban-bloqueio-dialog';

interface KanbanCartaoMenuProps {
  cartao: KanbanCartao;
  quadro: KanbanQuadro;
  onMover: (cartao: KanbanCartao, destino: DestinoMover) => void;
  onPrioridade: (cartao: KanbanCartao, nivel: NivelPrioridadeKanban) => void;
  onResolverConflito: (cartao: KanbanCartao) => void;
  onDesbloquear?: (cartao: KanbanCartao) => void;
}

const PRIORIDADES: readonly { nivel: NivelPrioridadeKanban; rotulo: string }[] = [
  { nivel: 'normal', rotulo: 'Normal' },
  { nivel: 'alta', rotulo: 'Alta' },
  { nivel: 'urgente', rotulo: 'Urgente' },
];

export function KanbanCartaoMenu({
  cartao,
  quadro,
  onMover,
  onPrioridade,
  onResolverConflito,
  onDesbloquear,
}: KanbanCartaoMenuProps) {
  const [dialogoBloqueio, setDialogoBloqueio] = useState(false);
  const { atualizar } = useKanbanAcoes();
  const destinos = destinosMoverPara(quadro, cartao);
  const emProducao = cartao.estado === 'EM_PRODUCAO';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="kanban-card-control inline-flex shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
          aria-label="Ações do cartão"
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Mover para…</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {emProducao ? (
              <DropdownMenuItem disabled>Pause antes de mover</DropdownMenuItem>
            ) : (
              destinos.map((destino) => (
                <DropdownMenuItem key={destino.celulaCodigo} onSelect={() => onMover(cartao, destino)}>
                  <span>{destino.titulo}</span>
                  {destino.compativel && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-600">
                      <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                      compatível
                    </span>
                  )}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Prioridade</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {PRIORIDADES.map((item) => (
              <DropdownMenuItem key={item.nivel} onSelect={() => onPrioridade(cartao, item.nivel)}>
                {item.rotulo}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {cartao.conflito && (
          <DropdownMenuItem onSelect={() => onResolverConflito(cartao)}>Resolver conflito</DropdownMenuItem>
        )}
        {cartao.bloqueio ? (
          <DropdownMenuItem onSelect={() => onDesbloquear?.(cartao)}>Desbloquear</DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={() => setDialogoBloqueio(true)}>Bloquear</DropdownMenuItem>
        )}
      </DropdownMenuContent>
      {dialogoBloqueio && cartao.osId && (
        // O diálogo vive em portal, mas os eventos do React sobem pela árvore de componentes: sem esta
        // barreira, cliques nele chegariam ao onClick do Card e abririam o painel lateral (WR-WEB-05).
        <div className="contents" onClick={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()}>
          <KanbanBloqueioDialog
            osId={cartao.osId}
            versao={cartao.versao}
            onClose={() => setDialogoBloqueio(false)}
            onSuccess={() => {
              setDialogoBloqueio(false);
              // Não depende de o SSE estar conectado.
              atualizar();
            }}
          />
        </div>
      )}
    </DropdownMenu>
  );
}
